import React, { useState } from "react";
import { PageShell, EditableSection } from "./PageShell";
import { Search, Package, Info } from "lucide-react";
import { toast } from "sonner";

const HELP = [{
  heading: "Where do I find my order number?",
  body: "Your order number is in the subject line of the confirmation email we sent right after checkout (‘Scentodore Order #SCT…’). If you can’t locate it, use the email address you used at checkout and any partial reference — we’ll look it up manually.",
}];

const NOTE = [{
  heading: "Live tracking coming soon",
  body: "We’re currently connecting real-time courier tracking. In the meantime, please check the tracking link in your shipping confirmation email, or contact our concierge for the latest status on your parcel.",
}];

export default function TrackOrderPage() {
  const [order, setOrder] = useState("");
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const onSearch = (e) => {
    e.preventDefault();
    if (!order || !email) return;
    setLoading(true);
    setResult(null);
    setTimeout(() => {
      setLoading(false);
      // Honest state: we don't have a real tracking integration yet.
      setResult({
        pending: true,
        title: "We’ve received your lookup",
        message: "Our tracking integration is being finalised. In the meantime, please check the shipping confirmation email we sent to " + email + " — the courier’s live tracking link is included there. If you can’t find it, contact our concierge with your order number and we’ll respond within a few business hours.",
      });
      toast.info("Please check your email for live tracking.");
    }, 500);
  };

  return (
    <PageShell
      slug="track-order"
      testid="track-order"
      eyebrow="Order Tracking"
      title="Where is my Scentodore?"
      intro="Enter your order number and email to be pointed to the latest tracking. Live courier updates are also emailed each time your parcel moves."
    >
      <div className="mb-8">
        <form onSubmit={onSearch} data-testid="track-order-form" className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 max-w-3xl">
          <input required value={order} onChange={(e) => setOrder(e.target.value)} placeholder="Order # (e.g. #SCT10241)" data-testid="track-order-number" className="px-4 py-3.5 rounded-xl border border-black/10 bg-[#FAF8F5] text-sm focus:outline-none focus:border-[#C5A059]" />
          <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email on the order" data-testid="track-order-email" className="px-4 py-3.5 rounded-xl border border-black/10 bg-[#FAF8F5] text-sm focus:outline-none focus:border-[#C5A059]" />
          <button type="submit" disabled={loading} data-testid="track-order-submit" className="inline-flex items-center justify-center gap-2 bg-[#1A1918] text-[#FAF8F5] px-6 py-3.5 rounded-xl text-xs uppercase tracking-[0.18em] hover:bg-[#C5A059] hover:text-[#0B0A08] transition-colors disabled:opacity-60">
            {loading ? "Looking…" : "Track"} <Search className="h-4 w-4" />
          </button>
        </form>

        {result ? (
          <div className="mt-6 max-w-3xl p-6 rounded-2xl border border-[#C5A059]/40 bg-[#F1ECE4]/60" data-testid="track-order-result">
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-full bg-[#C5A059]/15 flex items-center justify-center text-[#C5A059] shrink-0">
                <Info className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="font-serif text-lg text-[#1A1918]">{result.title}</div>
                <p className="text-[14px] text-[#1A1918]/75 mt-1.5 leading-[1.75]">{result.message}</p>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <EditableSection slug="track-order" sectionKey="help" fallback={HELP} />
      <EditableSection slug="track-order" sectionKey="note" fallback={NOTE} />
    </PageShell>
  );
}
